import { useCallback, useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { matchesTableSearch } from "@/lib/tableSearch";
import type { DocumentDto, DocumentKind, DocumentStatus } from "@gatiq/shared";
import { Pencil, Trash2, Upload } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Field,
  Modal,
  Page,
  PageHeader,
  SlideOver,
  TextArea,
  TextInput,
  RowActions,
  Table,
  TableSkeleton,
  THead,
  TH,
  TD,
  TR,
  Toolbar,
  ToolbarCount,
  useToast,
} from "@/ui";
import { api, ApiClientError } from "@/lib/api";
import { useCan } from "@/auth/access";

/** What the form tells the user, in the same MB the server counts. */
const MAX_MB = 10;
const IMAGE_TYPES = new Set(["image/png", "image/jpeg", "image/webp"]);

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () =>
      reject(reader.error instanceof Error ? reader.error : new Error("Could not read that file."));
    reader.readAsDataURL(file);
  });
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

interface FormState {
  title: string;
  description: string;
}

const EMPTY_FORM: FormState = { title: "", description: "" };

/**
 * Documents (D-23) — PDFs, images and links an org publishes to the field.
 *
 * The upload rides the form's own Save, the way the branding image does: the
 * file is read to a data URL and staged in state, and only the submit sends
 * it. That keeps a title typo from costing a second 10 MB upload.
 */
export function DocumentsPage() {
  const toast = useToast();
  const can = useCan();
  const canCreate = can("documents", "create");
  const canEdit = can("documents", "edit");
  const canDelete = can("documents", "delete");

  const [docs, setDocs] = useState<DocumentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<FormState>(EMPTY_FORM);
  const [createKind, setCreateKind] = useState<DocumentKind>("pdf");
  const [createFile, setCreateFile] = useState<File | null>(null);
  const [createLink, setCreateLink] = useState("");
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({});
  const [savingCreate, setSavingCreate] = useState(false);

  const [selected, setSelected] = useState<DocumentDto | null>(null);
  const [editForm, setEditForm] = useState<FormState>(EMPTY_FORM);
  const [editStatus, setEditStatus] = useState<DocumentStatus>("active");
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});
  const [savingEdit, setSavingEdit] = useState(false);

  const [toDelete, setToDelete] = useState<DocumentDto | null>(null);
  const [deleting, setDeleting] = useState(false);

  const createFileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<{ items: DocumentDto[] }>("/org/documents");
      setDocs(res.items);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not load documents.");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Clear only the errors for the fields this change touched. */
  const patchCreate = useCallback((patch: Partial<FormState>) => {
    setCreateForm((f) => ({ ...f, ...patch }));
    setCreateErrors((errs) => {
      const next = { ...errs };
      for (const key of Object.keys(patch)) delete next[key];
      return next;
    });
  }, []);

  const patchEdit = useCallback((patch: Partial<FormState>) => {
    setEditForm((f) => ({ ...f, ...patch }));
    setEditErrors((errs) => {
      const next = { ...errs };
      for (const key of Object.keys(patch)) delete next[key];
      return next;
    });
  }, []);

  /**
   * Refuse a wrong type or an oversize file before sending it to the server.
   */
  function onFileChange(e: ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (createKind === "pdf" ? file.type !== "application/pdf" : !IMAGE_TYPES.has(file.type)) {
      setCreateErrors((errs) => ({ ...errs, dataUrl: createKind === "pdf" ? "Choose a PDF file." : "Choose a PNG, JPEG or WebP image." }));
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      setCreateErrors((errs) => ({ ...errs, dataUrl: `That file is larger than ${MAX_MB} MB.` }));
      return;
    }
    setCreateFile(file);
    setCreateErrors((errs) => {
      const next = { ...errs };
      delete next.dataUrl;
      return next;
    });
    // A sensible default title from the file name, so nobody uploads "Untitled".
    if (!createForm.title.trim()) {
      const stem = file.name.replace(/\.(pdf|png|jpe?g|webp)$/i, "").replace(/[_-]+/g, " ").trim();
      if (stem) patchCreate({ title: stem });
    }
  }

  async function handleCreate(e: FormEvent): Promise<void> {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!createForm.title.trim()) errors.title = "Enter a title.";
    if (createKind === "link") {
      try {
        const url = new URL(createLink.trim());
        if (url.protocol !== "http:" && url.protocol !== "https:") throw new Error("Invalid protocol");
      } catch {
        errors.linkUrl = "Enter a valid http or https link.";
      }
    } else if (!createFile) {
      errors.dataUrl = `Choose ${createKind === "pdf" ? "a PDF" : "an image"} to upload.`;
    }
    setCreateErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSavingCreate(true);
    try {
      const dataUrl = createKind === "link" ? undefined : await fileToDataUrl(createFile!);
      await api.post<DocumentDto>("/org/documents", {
        title: createForm.title.trim(),
        description: createForm.description.trim() || undefined,
        kind: createKind,
        dataUrl,
        linkUrl: createKind === "link" ? createLink.trim() : undefined,
      });
      toast.success("Document published.");
      setCreateOpen(false);
      setCreateForm(EMPTY_FORM);
      setCreateKind("pdf");
      setCreateFile(null);
      setCreateLink("");
      setCreateErrors({});
      await load();
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.fields) setCreateErrors(err.fields);
        toast.error(err.message);
      } else {
        toast.error("Could not publish that document.");
      }
    } finally {
      setSavingCreate(false);
    }
  }

  function openEdit(doc: DocumentDto): void {
    setSelected(doc);
    setEditForm({ title: doc.title, description: doc.description ?? "" });
    setEditStatus(doc.status);
    setEditErrors({});
  }

  async function handleEdit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!selected) return;
    const errors: Record<string, string> = {};
    if (!editForm.title.trim()) errors.title = "Enter a title.";
    setEditErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSavingEdit(true);
    try {
      const res = await api.patch<DocumentDto>(`/org/documents/${selected.id}`, {
        title: editForm.title.trim(),
        description: editForm.description.trim() || null,
        status: editStatus,
      });
      toast.success("Document updated.");
      setSelected(null);
      setEditErrors({});
      setDocs((rows) => rows.map((d) => (d.id === res.id ? res : d)));
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.fields) setEditErrors(err.fields);
        toast.error(err.message);
      } else {
        toast.error("Could not update that document.");
      }
    } finally {
      setSavingEdit(false);
    }
  }

  async function handleDelete(): Promise<void> {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.del(`/org/documents/${toDelete.id}`);
      toast.success("Document withdrawn.");
      setToDelete(null);
      setDocs((rows) => rows.filter((d) => d.id !== toDelete.id));
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not withdraw that document.");
    } finally {
      setDeleting(false);
    }
  }

  const visible = docs.filter((document) => matchesTableSearch(q, document));

  return (
    <Page>
      <PageHeader
        title="Documents"
        description="PDFs, images and links for the field to open on their phones."
        action={
          canCreate ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Upload className="size-4" />
              Add document
            </Button>
          ) : undefined
        }
      />

      <Toolbar>
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search documents…"
          className="w-full max-w-xs rounded-md border border-rule bg-raised px-3 py-1.5 text-sm text-ink placeholder:text-muted"
          aria-label="Search documents"
        />
        <ToolbarCount count={visible.length} noun="document" />
      </Toolbar>

      <Card>
        {loading ? (
          <TableSkeleton rows={3} cols={4} />
        ) : visible.length === 0 ? (
          <EmptyState
            title={docs.length === 0 ? "No documents yet" : "Nothing matches that search"}
            message={
              docs.length === 0
                ? canCreate
                  ? "Add a PDF, image or link for the field to open."
                  : "Nothing has been published yet."
                : "Try a different title or file name."
            }
          />
        ) : (
          <Table searchable={false}>
            <THead>
              <TR>
                <TH>Title</TH>
                <TH>
                  <span className="hidden sm:inline">File</span>
                </TH>
                <TH>
                  <span className="hidden md:inline">Size</span>
                </TH>
                <TH>
                  <span className="hidden lg:inline">Published by</span>
                </TH>
                <TH>Status</TH>
                <TH width="2.5rem" />
              </TR>
            </THead>
            <tbody>
              {visible.map((doc) => (
                <TR key={doc.id}>
                  <TD>
                    <a className="font-medium text-ink hover:underline" href={`/api/org/documents/${doc.id}/file`} rel="noreferrer" target="_blank">{doc.title}</a>
                    {doc.description && (
                      <div className="mt-0.5 line-clamp-1 text-xs text-muted">{doc.description}</div>
                    )}
                    <div className="mt-0.5 text-xs text-muted sm:hidden">
                      {doc.kind === "link" ? "Link" : `${doc.fileName} · ${formatBytes(doc.bytes)}`}
                    </div>
                  </TD>
                  <TD className="hidden sm:table-cell">{doc.kind === "link" ? "External link" : doc.fileName}</TD>
                  <TD className="hidden md:table-cell">{doc.kind === "link" ? "—" : formatBytes(doc.bytes)}</TD>
                  <TD className="hidden lg:table-cell">
                    <div>{doc.uploadedByName}</div>
                    <div className="text-xs text-muted">{formatDate(doc.createdAt)}</div>
                  </TD>
                  <TD>
                    <Badge tone={doc.status === "active" ? "success" : "neutral"}>
                      {doc.status === "active" ? "Active" : "Inactive"}
                    </Badge>
                  </TD>
                  <TD>
                    <RowActions
                      items={[
                        ...(canEdit
                          ? [{ label: "Edit", icon: Pencil, onSelect: () => openEdit(doc) }]
                          : []),
                        ...(canDelete
                          ? [
                              {
                                label: "Withdraw",
                                icon: Trash2,
                                onSelect: () => setToDelete(doc),
                                danger: true,
                              },
                            ]
                          : []),
                      ]}
                      label={`Actions for ${doc.title}`}
                    />
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {createOpen && (
        <Modal open onClose={() => setCreateOpen(false)} title="Add document">
          <form className="space-y-4" onSubmit={handleCreate}>
            <TextInput
              label="Title"
              error={createErrors.title}
              required
              value={createForm.title}
              onChange={(e) => patchCreate({ title: e.target.value })}
              placeholder="Monsoon 2026 catalogue"
              maxLength={120}
            />

            <TextArea
              label="Description"
              hint="Optional. Shown under the title."
              value={createForm.description}
              onChange={(e) => patchCreate({ description: e.target.value })}
              placeholder="What this document covers"
              maxLength={300}
            />

            <fieldset>
              <legend className="text-sm font-medium text-ink">Type</legend>
              <div className="mt-2 flex flex-wrap gap-2">
                {(["pdf", "image", "link"] as const).map((kind) => (
                  <label key={kind} className={`cursor-pointer rounded-md border px-3 py-1.5 text-sm focus-within:ring-2 focus-within:ring-brand ${createKind === kind ? "border-brand bg-brand-light text-brand-ink" : "border-rule bg-raised text-ink"}`}>
                    <input
                      type="radio"
                      name="document-kind"
                      value={kind}
                      checked={createKind === kind}
                      onChange={() => {
                        setCreateKind(kind);
                        setCreateFile(null);
                        setCreateErrors({});
                      }}
                      className="sr-only"
                    />
                    {kind === "pdf" ? "PDF" : kind === "image" ? "Image" : "Link"}
                  </label>
                ))}
              </div>
            </fieldset>

            {createKind === "link" ? (
              <TextInput
                label="Link URL"
                error={createErrors.linkUrl}
                required
                type="url"
                value={createLink}
                onChange={(e) => {
                  setCreateLink(e.target.value);
                  setCreateErrors((errs) => ({ ...errs, linkUrl: "" }));
                }}
                placeholder="https://example.com/catalogue"
              />
            ) : (
              <div>
                <label className="text-sm font-medium text-ink" htmlFor="document-file">
                  {createKind === "pdf" ? "PDF" : "Image"}
                </label>
                <div className="mt-1 flex items-center gap-3">
                  <input
                    ref={createFileRef}
                    type="file"
                    accept={createKind === "pdf" ? "application/pdf" : "image/png,image/jpeg,image/webp"}
                    className="sr-only"
                    id="document-file"
                    onChange={onFileChange}
                  />
                  <label
                    htmlFor="document-file"
                    className="cursor-pointer inline-flex items-center gap-1.5 rounded-md border border-rule bg-raised px-3 py-1.5 text-sm font-medium text-ink shadow-card transition-colors hover:bg-panel"
                  >
                    <Upload className="size-4" />
                    Choose file
                  </label>
                  <span className="text-xs text-muted">
                    {createFile ? createFile.name : `${createKind === "pdf" ? "PDF" : "PNG, JPEG or WebP"} · max ${MAX_MB} MB`}
                  </span>
                </div>
                {createErrors.dataUrl && <p className="mt-1 text-xs text-danger">{createErrors.dataUrl}</p>}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={savingCreate}>
                Publish
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {selected && (
        <SlideOver open onClose={() => setSelected(null)} title="Edit document">
          <form className="space-y-4" onSubmit={handleEdit}>
            <TextInput
              label="Title"
              error={editErrors.title}
              required
              value={editForm.title}
              onChange={(e) => patchEdit({ title: e.target.value })}
              maxLength={120}
            />

            <TextArea
              label="Description"
              hint="Optional. Shown under the title."
              value={editForm.description}
              onChange={(e) => patchEdit({ description: e.target.value })}
              maxLength={300}
            />

            <Field label="Status" hint="An inactive document stays in the list but is not offered to the field.">
              {(id) => (
                <select
                  id={id}
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value as DocumentStatus)}
                  className="w-full rounded-md border border-rule bg-raised px-3 py-1.5 text-sm text-ink"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              )}
            </Field>

            <p className="text-xs text-muted">
              The {selected.kind === "link" ? "link" : "file"} is fixed once published — to replace it, withdraw this document and add a new one.
            </p>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setSelected(null)}>
                Cancel
              </Button>
              <Button type="submit" loading={savingEdit}>
                Save
              </Button>
            </div>
          </form>
        </SlideOver>
      )}

      <ConfirmDialog
        open={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={handleDelete}
        title="Withdraw document"
        message={
          toDelete
            ? `Withdraw "${toDelete.title}"? It stops being offered to the field, but stays in the audit log.`
            : ""
        }
        confirmLabel="Withdraw"
        loading={deleting}
      />
    </Page>
  );
}
